import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import TicketList, { ListOptions } from "./TicketList";
import { DEFAULT_LIST_SORT } from "../../listSort";
import TicketEdit from "./TicketEdit";
import { hasAnyRole, isTechnician, isCustomer } from "../../utils/roles";
import { MDSplitView } from "@solsys/solsys-reactjs";

export default function TagController({
  navigate,
  action,
  id,
  search,
  style,
  options = {},
}) {
  const { user } = useAuth();
  const canEditTickets = hasAnyRole(user, ["administrator", "technician"]);
  const canCreateTickets = canEditTickets || isCustomer(user);
  const isTech = isTechnician(user);
  const isCust = isCustomer(user);
  const underCrane = Boolean(options.crane_id);
  // Techs (and nested admin/tech crane lists) default to open. Customers seeing
  // a crane's tickets should see both open and closed unless they filter.
  const defaultStatusId = (underCrane && !isCust) || isTech ? "1" : null;

  let listState = {
    [ListOptions.sortBy]: DEFAULT_LIST_SORT.sortBy,
    [ListOptions.sortDirection]: DEFAULT_LIST_SORT.sortDirection,
    [ListOptions.name]: null,
    statusId: defaultStatusId,
    subscribedOnly: underCrane ? false : Boolean(isTech),
  };
  const didMount = useRef(false);
  useEffect(() => (didMount.current ? undefined : (didMount.current = true)));
  if (!didMount.current && Object.keys(search).length) {
    Object.keys(ListOptions).forEach((key) => {
      listState[key] = search[key];
    });
  }

  listState = useState(listState);

  // Under crane: stay on list so narrow/mobile layouts show tickets first.
  // Standalone: default empty route to add (existing top-level behavior).
  const effectiveAction = underCrane
    ? (action || (id ? "edit" : "list"))
    : ((!action && !id) ? "add" : action);

  return (
    <div className="tags">
      <MDSplitView
        globalName="TagMDV"
        style={ style }
        action={ effectiveAction }
        master={ ({ isNarrow, autoSelect }) => (
          <TicketList
            isMaster={ canCreateTickets }
            state={ listState }
            id={ id }
            action={ effectiveAction }
            options={ options }
            onStateChange={ (state) => {
              navigate({
                action: effectiveAction,
                id,
                search: state,
                push: false,
              });
            } }
            onSelect={ (newId, userInteraction) => {
              // Stay nested under crane so options.crane_id is preserved.
              const popOut = underCrane ? false : isNarrow;
              if (
                newId &&
                id &&
                String(newId) === String(id) &&
                userInteraction
              ) {
                navigate({
                  popOut,
                  action: "list",
                  id: null,
                  search: listState[0],
                });
              } else {
                navigate({
                  popOut,
                  ...(newId === 0
                    ? {
                      action: "add",
                      id: null,
                    }
                    : {
                      action: "edit",
                      id: newId,
                    }),
                  search: listState[0],
                });
              }
            } }
          />
        ) }
        detail={ () => {
          const isAdd = effectiveAction === "add";
          return (
            <TicketEdit
              key={ `action_${effectiveAction}_id_${id}` }
              id={ id }
              action={ effectiveAction }
              navigate={ navigate }
              options={ options }
              readOnly={ isAdd ? !canCreateTickets : !canEditTickets }
            />
          );
        } }
        initialWidth={ 300 }
        maxWidth={ 800 }
        minWidth={ 290 }
      />
    </div>
  );
}
